import { useMemo } from "react"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { useNetworksStateContext, useNetworkSubnetworksLength } from "@l2r-front/l2r-networks"
import { Typography, useMediaQuery, theme, TruncableText } from "@l2r-front/l2r-ui"
import { formatMetersToKilometers } from "@l2r-front/l2r-utils"

import { appRoad } from "../../../../common/constants/appRoad"
import { I18N_NAMESPACE } from "../../../../common/constants/i18n"

import * as Styled from "./Dashboard.styled"

const GridAreas = [
    "1 / 1 / 3 / 1",
    "1 / 2 / 3 / 2",
    "3 / 1 / 3 / 3",
]

export const Dashboard = () => {
    const { selectedNetwork, selectedSubNetworkSlugs } = useNetworksStateContext()
    const { t } = useTranslation(I18N_NAMESPACE)
    const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"))

    const { data: selectionLength, isLoading: isSelectionLengthLoading, isError: isSelectionLengthError } = useNetworkSubnetworksLength()

    const actualNetwork = useMemo(() => {
        if (!selectedNetwork) {
            return null
        }
        return (!selectedNetwork.role && selectedNetwork.children.length === 1)
            ? selectedNetwork.children[0]
            : selectedNetwork
    }, [selectedNetwork])

    const { data: allSubNetworksOfActualNetworkLength, isLoading: isActualNetworkLengthLoading, isError: isActualNetworkLengthError } = useNetworkSubnetworksLength(actualNetwork?.children?.map(child => child.slug))

    const totalNetworkLength = useMemo(() => {
        if (actualNetwork?.length) {
            return formatMetersToKilometers(actualNetwork.length)
        }
        return (isActualNetworkLengthLoading || isActualNetworkLengthError) ? "" : allSubNetworksOfActualNetworkLength ? formatMetersToKilometers(allSubNetworksOfActualNetworkLength.totalLength) : ""
    }, [isActualNetworkLengthLoading, isActualNetworkLengthError, actualNetwork, allSubNetworksOfActualNetworkLength])

    const totalSelectionLength = useMemo(() => {
        return (isSelectionLengthLoading || isSelectionLengthError) ? "" : selectionLength ? formatMetersToKilometers(selectionLength.totalLength) : ""
    }, [isSelectionLengthLoading, isSelectionLengthError, selectionLength])

    const subNetworkLengthes = useMemo(() => {
        if (!selectedNetwork) {
            return null
        }
        if (!selectedNetwork.role && selectedNetwork.children.length === 1) {
            return null
        }
        if (selectedSubNetworkSlugs && selectedSubNetworkSlugs.length !== selectedNetwork.children.length) {
            return selectedSubNetworkSlugs.sort((snA, snB) => snA.localeCompare(snB))
                .reduce((acc, slug) => {
                    const child = selectedNetwork.children.find(child => child.slug === slug)
                    if (child) {
                        if (acc.length > 0) {
                            acc.push("•")
                        }
                        acc.push(<Typography variant="Small">{child.name}</Typography>)
                        acc.push(<Styled.LengthText variant="Small">{formatMetersToKilometers(child.length)}</Styled.LengthText>)
                    }
                    return acc
                }, [])
        }
        return null
    }, [selectedNetwork, selectedSubNetworkSlugs])

    const LengthesBlock = useMemo(() => {
        return <Styled.LengthContainer isLargeScreen={isLargeScreen}>
            <Styled.NetworkLength isLargeScreen={isLargeScreen}>
                <Typography variant="Desktop & Mobile/Heading/H3">
                    {`${t(I18N_NAMESPACE, "components.networkLength.label")}`}
                </Typography>
                <Typography variant="Desktop & Mobile/Heading/H1">
                    {totalNetworkLength}
                </Typography>
            </Styled.NetworkLength>
            {!!subNetworkLengthes?.length && <>
                <Styled.SelectionLength>
                    <Styled.DomainIcon />
                    <Typography variant="Desktop & Mobile/Heading/H3">
                        {t(I18N_NAMESPACE, "components.subNetworksLength.label")}
                    </Typography>
                    <Styled.LengthText>
                        {totalSelectionLength}
                    </Styled.LengthText>
                </Styled.SelectionLength>
                <TruncableText maxHeight={80}>
                    <Styled.SubNetworkLengthesContent>
                        {subNetworkLengthes}
                    </Styled.SubNetworkLengthesContent>
                </TruncableText>
            </>}
        </Styled.LengthContainer>
    }, [totalNetworkLength, isLargeScreen, subNetworkLengthes, totalSelectionLength, t])

    const activeModules = useMemo(() => {
        if (selectedNetwork?.children?.length) {
            const childModules = selectedNetwork.children.reduce((acc, child) => {
                if (child.modules) {
                    acc.push(...child.modules)
                }
                return acc
            }, [])
            return Object.values(appRoad.modules).filter(module => childModules.includes(module.id))
        }
        return selectedNetwork ? Object.values(appRoad.modules).filter(module => selectedNetwork.modules.includes(module.id)) : []
    }, [selectedNetwork])

    const kpis = useMemo(() => {
        return Object.values(activeModules)
            .reduce((kpisArray, module) => {
                if (module.kpis) {
                    kpisArray.push(module.kpis)
                }

                return kpisArray.flat()
            }, [])
            .sort((a, b) => a.gridIndex - b.gridIndex)
    }, [activeModules])

    const sidebarKpis = useMemo(() => {
        return kpis
            .sort((a, b) => a.position - b.position)
            .filter(kpi => kpi.parent === "sidebar")
            .map((kpi, index) => {
                const Component = kpi.component
                return <Component key={`kpi-${index}`} style={{ height: "fit-content" }} />
            })
    }, [kpis])

    const listKpis = useMemo(() => {
        if (kpis) {
            if (!isLargeScreen) {
                const mobileDashboard = kpis
                    .sort((a, b) => a.mobileOrder - b.mobileOrder)
                    .map((kpi, index) => {
                        const Component = kpi.component
                        return <Component key={`kpi-${index}`} style={{
                            minHeight: kpi.mobileHeight,
                        }} />
                    })
                return mobileDashboard
            }
            return kpis
                .filter(kpi => kpi.parent === "list")
                .map((kpi, index) => {
                    const Component = kpi.component
                    return <Component key={`kpi-${index}`} style={{
                        gridArea: kpi.gridIndex < 2 ? GridAreas[index] : GridAreas[kpi.gridIndex],
                        height: kpi.height,
                    }} />
                })
        }
    }, [kpis, isLargeScreen])

    const desktopLayout = useMemo(() => {
        return <>
            <Styled.Sidebar>
                <Styled.NetworkName id="network-name" variant="Desktop & Mobile/Heading/H1" isLargeScreen={isLargeScreen}>
                    {actualNetwork ? isLargeScreen ? actualNetwork.nameLong : actualNetwork.name : ""}
                </Styled.NetworkName>
                <Styled.PanelWrapper>
                    {LengthesBlock}
                    <Styled.KpiContainer>
                        {sidebarKpis}
                    </Styled.KpiContainer>
                </Styled.PanelWrapper>
            </Styled.Sidebar>
            <Styled.Container>
                <Styled.GridContainer>
                    {kpis.length ? listKpis : (
                        <Typography id="noActiveApplications-text" variant="Desktop & Mobile/Body/Small">
                            {t(I18N_NAMESPACE, "pages.messages.noActiveApplications")}
                        </Typography>
                    )}
                </Styled.GridContainer>
            </Styled.Container>
        </>
    }, [actualNetwork, isLargeScreen, sidebarKpis, listKpis, kpis, LengthesBlock, t])

    const mobileLayout = useMemo(() => {
        return <Styled.FlexContainer>
            <Styled.NetworkName id="network-name" variant="Desktop & Mobile/Heading/H1" isLargeScreen={isLargeScreen}>
                {actualNetwork ? isLargeScreen ? actualNetwork.nameLong : actualNetwork.name : ""}
            </Styled.NetworkName>
            <Styled.Scrollable>
                {LengthesBlock}
                {kpis.length ? listKpis : (
                    <Typography id="noActiveApplications-text" variant="Desktop & Mobile/Body/Small">
                        {t(I18N_NAMESPACE, "pages.messages.noActiveApplications")}
                    </Typography>
                )}
            </Styled.Scrollable>
        </Styled.FlexContainer >
    }, [actualNetwork, isLargeScreen, listKpis, kpis, LengthesBlock, t])

    return (
        <Styled.PanelContent>
            {isLargeScreen ? desktopLayout : mobileLayout}
        </Styled.PanelContent>
    )
}